import React from 'react';
import { Badge, Table } from 'react-bootstrap';
import { ChevronRight } from 'lucide-react';
import { PLATFORM_TEXT } from '../../../types/activity';
import type { BuildingRow, UserRow, UserSortKey } from './userActivityView';

const SORT_LABELS: Array<{ key: UserSortKey; label: string }> = [
    { key: 'name', label: 'Användare' },
    { key: 'lastActive', label: 'Senast aktiv' },
    { key: 'activeDays', label: 'Aktiva dagar' },
    { key: 'actions', label: 'Åtgärder' },
];

interface UserTableProps {
    rows: UserRow[];
    sortKey: UserSortKey;
    onSort: (key: UserSortKey) => void;
    /** Opens the user's detail; pass the click so Cmd/Ctrl/middle-click opens a new tab. */
    onOpen: (id: string, event?: React.MouseEvent) => void;
}

export const UserActivityTable: React.FC<UserTableProps> = ({ rows, sortKey, onSort, onOpen }) => (
    <Table hover className="user-activity-table">
        <thead>
            <tr>
                {SORT_LABELS.map(({ key, label }) => (
                    <th key={key}>
                        <button
                            type="button"
                            className={`user-activity-sort ${sortKey === key ? 'active' : ''}`}
                            onClick={() => onSort(key)}
                            aria-pressed={sortKey === key}
                        >
                            {label}
                        </button>
                    </th>
                ))}
                <th>Plattform</th>
                <th aria-label="Öppna" />
            </tr>
        </thead>
        <tbody>
            {rows.map((row) => (
                <tr
                    key={row.id}
                    onClick={(e) => onOpen(row.id, e)}
                    onAuxClick={(e) => onOpen(row.id, e)}
                    className="user-activity-row"
                >
                    <td className="ua-cell-name">
                        <span className="ua-name">{row.name}</span>
                        {row.external && <Badge bg="light" text="dark" className="ms-2">Extern</Badge>}
                        {row.superadmin && <Badge bg="light" text="dark" className="ms-2">Superadmin</Badge>}
                    </td>
                    <td className="ua-cell-last">
                        <span className="ua-cell-label">Senast aktiv</span>
                        {row.lastActive ? row.lastActive.slice(0, 16) : <span className="text-muted">Inte aktiv</span>}
                    </td>
                    <td className="ua-cell-days">
                        <span className="ua-cell-label">Aktiva dagar</span>
                        {row.activeDays}
                    </td>
                    <td className="ua-cell-actions">
                        <span className="ua-cell-label">Åtgärder</span>
                        {row.actions}
                    </td>
                    <td className="ua-cell-platform">
                        <span className="ua-cell-label">Plattform</span>
                        {row.platforms.map((p) => PLATFORM_TEXT[p]).join(', ') || '–'}
                    </td>
                    <td className="ua-cell-open"><ChevronRight size={16} /></td>
                </tr>
            ))}
        </tbody>
    </Table>
);

interface BuildingTableProps {
    rows: BuildingRow[];
    buildingName: (key: string) => string;
}

export const BuildingActivityTable: React.FC<BuildingTableProps> = ({ rows, buildingName }) => (
    <Table className="user-activity-table building-activity-table">
        <thead>
            <tr>
                <th>Byggnad</th>
                <th>Åtgärder</th>
                <th>Aktiva användare</th>
                <th>Senast</th>
            </tr>
        </thead>
        <tbody>
            {rows.map((row) => (
                <tr key={row.buildingKey}>
                    <td className="ua-cell-name"><span className="ua-name">{buildingName(row.buildingKey)}</span></td>
                    <td className="ua-cell-actions"><span className="ua-cell-label">Åtgärder</span>{row.actions}</td>
                    <td className="ua-cell-days"><span className="ua-cell-label">Aktiva användare</span>{row.activeUsers}</td>
                    <td className="ua-cell-last"><span className="ua-cell-label">Senast</span>{row.lastActive.slice(0, 16)}</td>
                </tr>
            ))}
        </tbody>
    </Table>
);
